import { useState } from "react";
import { ChartLineUp } from "@phosphor-icons/react";
import { EmptyState } from "../shared/EmptyState";

const RATING_COLORS = {
  "A+": "border-emerald-400/40 bg-emerald-400/10 text-emerald-300",
  A: "border-emerald-400/30 bg-emerald-400/5 text-emerald-300",
  "B+": "border-sky-400/30 bg-sky-400/5 text-sky-300",
  B: "border-sky-400/20 bg-sky-400/5 text-sky-300",
  "C+": "border-zinc-400/30 bg-zinc-400/5 text-zinc-300",
  C: "border-zinc-400/20 bg-zinc-400/5 text-zinc-400",
  PASS: "border-white/10 bg-white/5 text-zinc-500",
};

const formatAmerican = (price) => (price > 0 ? `+${price}` : `${price}`);

// Promoted to be the FULL GAME O/U primary hero on GameDetail (was previously nested inside the
// Prediction card as "TotalSection"). Same exact values/logic as before -- only the surrounding
// JSX/labels were relocated for the progressive-disclosure page reorg; no calculation changed.
export const FullGameOUCard = ({ total, testId, onCalculateTotal }) => {
  const [thresholdInput, setThresholdInput] = useState("");

  if (!total) {
    return (
      <div data-testid={testId} className="flex flex-col gap-3">
        <EmptyState icon={ChartLineUp} title="Full Game O/U Unavailable" description="Loading prediction data." testId={`${testId}-empty`} />
      </div>
    );
  }

  const totalOk = total.status === "ok";
  const market = total.market_line;
  const edge = total.edge;
  const usingMarketLine = market && total.total_threshold === market.line_value;

  return (
    <div data-testid={testId} className="flex flex-col gap-3">
      {totalOk ? (
        <>
          <div className="rounded-lg border border-white/10 bg-eb-bg p-4 text-center">
            <span className="font-mono text-[10px] uppercase tracking-wide text-zinc-500">EdgeBall Projected Total Runs</span>
            <p data-testid={`${testId}-projected-total`} className="mt-1 font-heading text-3xl text-white">
              {total.projected_total.toFixed(2)}
            </p>
            <p data-testid={`${testId}-breakdown`} className="mt-1 font-mono text-[11px] text-zinc-500">
              {total.expected_home_runs.toFixed(2)} projected home runs + {total.expected_away_runs.toFixed(2)} projected away runs
              {total.park_adjustment_applied
                ? ` (park factor index ${total.park_run_factor.toFixed(1)}, 100 = neutral)`
                : " (no park adjustment — park data unavailable)"}
            </p>
          </div>

          {market ? (
            <div data-testid={`${testId}-market-line`} className="flex items-center justify-between rounded-lg border border-white/10 bg-eb-bg p-3">
              <div>
                <span className="font-mono text-[10px] uppercase tracking-wide text-zinc-500">Market Total ({market.sportsbook})</span>
                <p className="mt-0.5 font-mono text-sm text-white">
                  {market.line_value} &mdash; O {formatAmerican(market.over_price)} / U {formatAmerican(market.under_price)}
                </p>
              </div>
              {edge && (
                <div className={`rounded-md border px-3 py-1.5 text-center font-mono text-xs uppercase tracking-wide ${RATING_COLORS[edge.bet_rating] || RATING_COLORS.PASS}`} data-testid={`${testId}-bet-rating`}>
                  <div className="text-[10px] text-zinc-500">
                    {edge.recommended_side ? `Lean ${edge.recommended_side}` : "No Edge"}
                  </div>
                  <div className="text-base font-heading">{edge.bet_rating}</div>
                </div>
              )}
            </div>
          ) : (
            <p data-testid={`${testId}-no-market-line`} className="font-mono text-xs text-zinc-400">
              No sportsbook total line synced yet for this game.
            </p>
          )}

          <div className="flex flex-col gap-2">
            <label htmlFor={`${testId}-threshold-input`} className="font-mono text-[10px] uppercase tracking-wide text-zinc-500">
              {market ? "Or enter your own total to compare against (no edge shown for a hypothetical line)" : "Enter a total (e.g. 8.5) to calculate Over/Under probabilities"}
            </label>
            <div className="flex gap-2">
              <input
                id={`${testId}-threshold-input`}
                data-testid={`${testId}-threshold-input`}
                type="number"
                step="0.5"
                value={thresholdInput}
                onChange={(e) => setThresholdInput(e.target.value)}
                placeholder={market ? String(market.line_value) : "e.g. 8.5"}
                className="w-32 rounded-lg border border-white/10 bg-eb-bg px-3 py-2 font-mono text-sm text-white outline-none focus:border-white/30"
              />
              <button
                type="button"
                data-testid={`${testId}-threshold-submit-btn`}
                onClick={() => onCalculateTotal && onCalculateTotal(thresholdInput)}
                className="rounded-lg border border-white/10 bg-white/5 px-4 py-2 font-mono text-xs uppercase tracking-wide text-zinc-200 transition-colors hover:bg-white/10"
              >
                Calculate
              </button>
            </div>
          </div>

          {total.threshold_status === "ok" ? (
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-lg border border-white/10 bg-eb-bg p-3 text-center">
                <span className="font-mono text-[10px] uppercase tracking-wide text-zinc-500">Over {total.total_threshold}</span>
                <p data-testid={`${testId}-over-probability`} className="mt-1 font-heading text-xl text-white">
                  {(total.over_probability * 100).toFixed(1)}%
                </p>
                {edge && <p className="font-mono text-[10px] text-zinc-500">edge {(edge.over_edge_percentage * 100).toFixed(1)}%</p>}
              </div>
              <div className="rounded-lg border border-white/10 bg-eb-bg p-3 text-center">
                <span className="font-mono text-[10px] uppercase tracking-wide text-zinc-500">Under {total.total_threshold}</span>
                <p data-testid={`${testId}-under-probability`} className="mt-1 font-heading text-xl text-white">
                  {(total.under_probability * 100).toFixed(1)}%
                </p>
                {edge && <p className="font-mono text-[10px] text-zinc-500">edge {(edge.under_edge_percentage * 100).toFixed(1)}%</p>}
              </div>
              <div className="rounded-lg border border-white/10 bg-eb-bg p-3 text-center">
                <span className="font-mono text-[10px] uppercase tracking-wide text-zinc-500">Push (Exact)</span>
                <p data-testid={`${testId}-push-probability`} className="mt-1 font-heading text-xl text-white">
                  {(total.push_probability * 100).toFixed(1)}%
                </p>
              </div>
            </div>
          ) : (
            <p data-testid={`${testId}-no-threshold`} className="font-mono text-xs text-zinc-400">
              Enter a total above and press Calculate to see Over/Under probabilities. No probability is shown without one.
            </p>
          )}
          <p data-testid={`${testId}-meta`} className="font-mono text-[10px] text-zinc-500">
            Model: {total.model_version} &mdash; Poisson distribution over combined runs, not a trained/calibrated model.
            {usingMarketLine ? " Edge/EV compare the model's own probability to the no-vig fair price at the real market line above." : ""}
            {" "}Bet rating is a heuristic labeling scale, not a fitted/calibrated statistic.
          </p>
        </>
      ) : market ? (
        <>
          <div data-testid={`${testId}-market-line`} className="flex items-center justify-between rounded-lg border border-white/10 bg-eb-bg p-3">
            <div>
              <span className="font-mono text-[10px] uppercase tracking-wide text-zinc-500">Market Total ({market.sportsbook})</span>
              <p className="mt-0.5 font-mono text-sm text-white">
                {market.line_value} &mdash; O {formatAmerican(market.over_price)} / U {formatAmerican(market.under_price)}
              </p>
            </div>
          </div>
          <p data-testid={`${testId}-model-held`} className="font-mono text-xs text-zinc-500">
            EdgeBall&rsquo;s own total projection isn&rsquo;t available for this league yet &mdash; real stat inputs
            (starter/bullpen quality) aren&rsquo;t connected. {total.reason ? `(${total.reason})` : ""} The line above is the real
            bookmaker total only, not a model prediction.
          </p>
        </>
      ) : (
        <EmptyState
          icon={ChartLineUp}
          title="Insufficient Data For A Total Projection"
          description={total.reason || "Not enough data to project a total yet."}
          testId={`${testId}-insufficient`}
        />
      )}
    </div>
  );
};
